import { useEffect, useState } from "react";

type VersionInfo = {
	version: string;
	buildTime: string;
};

type UseVersionCheckResult = {
	hasNewVersion: boolean;
	currentVersion: string | null;
	latestVersion: string | null;
	checkForUpdate: () => Promise<void>;
	isChecking: boolean;
};

const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || "unknown";
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 Minuten

export const useVersionCheck = (): UseVersionCheckResult => {
	const [hasNewVersion, setHasNewVersion] = useState(false);
	const [latestVersion, setLatestVersion] = useState<string | null>(null);
	const [isChecking, setIsChecking] = useState(false);

	const checkForUpdate = async () => {
		if (isChecking) return;

		setIsChecking(true);
		try {
			// Füge Timestamp hinzu, um Browser-Cache zu umgehen
			const timestamp = new Date().getTime();
			const response = await fetch(`/version.json?t=${timestamp}`, {
				cache: "no-store",
				headers: {
					"Cache-Control": "no-cache",
					Pragma: "no-cache",
				},
			});

			if (!response.ok) {
				console.warn("Version check failed:", response.statusText);
				return;
			}

			const versionInfo: VersionInfo = await response.json();
			setLatestVersion(versionInfo.version);

			// Prüfe, ob eine neue Version verfügbar ist
			if (versionInfo.version !== CURRENT_VERSION && CURRENT_VERSION !== "unknown") {
				console.log(
					`Neue Version verfügbar: ${versionInfo.version} (aktuell: ${CURRENT_VERSION})`,
				);
				setHasNewVersion(true);
			}
		} catch (error) {
			console.error("Error checking for version update:", error);
		} finally {
			setIsChecking(false);
		}
	};

	useEffect(() => {
		// Initiale Prüfung nach 10 Sekunden (damit die App zuerst laden kann)
		const initialTimeout = setTimeout(() => {
			checkForUpdate();
		}, 10000);

		// Periodische Prüfung alle 5 Minuten
		const interval = setInterval(() => {
			checkForUpdate();
		}, CHECK_INTERVAL);

		return () => {
			clearTimeout(initialTimeout);
			clearInterval(interval);
		};
	}, []);

	return {
		hasNewVersion,
		currentVersion: CURRENT_VERSION,
		latestVersion,
		checkForUpdate,
		isChecking,
	};
};
